import { Table, TableBody, TableCell, TableRow } from '@mui/material'
import { useState } from 'react'
import ApiClient from '../../backend/ApiClient'
import { IQualityMeasureFilter, IQualityMeasures, IQualityMeasuresProvider, IQualityMeasuresScoringBand } from '../../definitions/QualityMeasures'
import Helper from '../../helpers/Helper'
import TimeHelper from '../../helpers/TimeHelper'
import ActivityManager from '../../managers/ActivityManager'
import { useAppSelector } from '../../store/Hooks'
import SmartBadgeFilter from '../badge_filter/SmartBadgeFilter'
import BulletChart from '../charts/BulletChart'
import RouteHeader from '../controls/RouteHeader'
import SmartCard from '../controls/cards/SmartCard'
import KPICard from '../controls/data_visualization/KPICard'
import StaleObserver from '../controls/stale/StaleObserver'
import SmartTable from '../controls/table/SmartTable'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import Tile from '../layout/Tile'
import RoutingManager from '../routing/Routing'
import { Row, SmartTableUtilities } from '../controls/table'

export default function QualityMeasuresBreakdown() {
    const params = RoutingManager.getParams<{ contractId: number }>(useAppSelector(s => s.session.routeParams))
    const time = useAppSelector(s => s.app.timeSettings ?? TimeHelper.timeSettings())
    const [filter, setFilter] = useState<IQualityMeasureFilter>({
        qualityMeasureIds: []
    })

    const { status, response: qualityMeasuresBreakdown } = useQuery({
        queryKey: buildQueryKey('qualityMeasures', params.contractId, TimeHelper.stringify(time)),
        queryFn: () => ApiClient.getContractQualityMeasures(params.contractId, time),
        cache: true
    })

    const { status: statusQualityMeasuresKPI, response: qualityMeasuresKPI } = useQuery({
        queryKey: buildQueryKey('quality-measures-kpi', params.contractId, TimeHelper.stringify(time), Helper.generateKeyOfObject(filter)),
        queryFn: async () => await ApiClient.getQualityMeasuresKPI(params.contractId, time, filter),
        cache: true
    })

    const provider = (provider: IQualityMeasuresProvider, scoringBands: IQualityMeasuresScoringBand[]) => {
        const { score } = provider
        const isInverse = scoringBands.some(x => x.inverse)
        const first = { name: '', fixedWidth: 10 }
        const bands = isInverse ? [...scoringBands, first] : [first, ...scoringBands]

        return (
            <BulletChart value={score / 100} isInverse={isInverse} bands={bands} />
        )
    }

    const providerRow = (qualityMeasureDetails: IQualityMeasures, i: number): Row => {
        return SmartTableUtilities.toRow(
            [
                { value: qualityMeasureDetails.name },
                { value: qualityMeasureDetails.score + '%' },
                { value: qualityMeasureDetails.denominator }
            ],
            i,
            qualityMeasureDetails.providers?.length > 0
                ? qualityMeasureSubContent(qualityMeasureDetails)
                : undefined
        )
    }

    const qualityMeasureSubContent = (qualityMeasure: IQualityMeasures) => {
        return (
            <Table>
                <TableBody>
                    {qualityMeasure?.providers?.map(x => {
                        return (
                            <TableRow key={x.id}>
                                <TableCell width='20%'>
                                    {x.name}
                                </TableCell>
                                <TableCell>
                                    {provider(x, qualityMeasure.scoringBands)}
                                </TableCell>
                                <TableCell width='10%'>
                                    {x.numerator} out of {x.denominator} members
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
        )
    }

    const qmLoading = status === 'loading'
    const kpiLoading = statusQualityMeasuresKPI === 'loading'

    const filteredQualityMeasure = (qualityMeasuresBreakdown?.qualityMeasures ?? []).filter(x => filter.qualityMeasureIds.length === 0 || filter.qualityMeasureIds.includes(x.id))

    return (
        <Tile.Container>
            <RouteHeader
                primary='Quality Measures Breakdown'
                timeSettings={time}
                subActions={[{ label: 'Updated last week', icon: 'clock', onClick: () => ActivityManager.openActivity('qualityMeasures') }]}
            />
            <Tile.Row flex={0} spacing={2}>
                <SmartCard flex={1}>
                    <KPICard
                        label='Quality Measures avg. Scores'
                        isLoading={kpiLoading}
                        unit='%'
                        data={Helper.dataframeToKPICard(qualityMeasuresKPI?.avgScores)}
                    />
                </SmartCard>
                <SmartCard flex={1}>
                    <KPICard
                        label='Population'
                        isLoading={kpiLoading}
                        data={Helper.dataframeToKPICard(qualityMeasuresKPI?.attributedMembers)}
                    />
                </SmartCard>
            </Tile.Row>
            <Tile.Row flex={0} spacing={1}>
                <SmartBadgeFilter
                    name='Quality Measures'
                    values={filter.qualityMeasureIds.map(String)}
                    contractId={params.contractId}
                    onApply={x => setFilter(f => ({ ...f, qualityMeasureIds: x.map(x => Number(x)) }))}
                />
            </Tile.Row>
            <SmartCard flex={1} style={{ overflowY: 'auto' }}>
                <SmartTable
                    isLoading={qmLoading}
                    columns={[
                        { id: 'action', label: '' },
                        { id: 'qualitymeasures', label: 'Quality Measures' },
                        { id: 'compositeScore', label: 'Composite Score', numeric: true },
                        { id: 'membersPopulation', label: 'Members Population', numeric: true }
                    ]}
                    datasource={{ rows: filteredQualityMeasure?.map((x, i) => providerRow(x, i))
                    }}
                />
            </SmartCard>
            <StaleObserver status={status} />
        </Tile.Container>
    )
}
